/**
 * Matchday Loans API Service (Créditos por Jornada)
 * Conecta con los endpoints oficiales del gateway y maneja mock fallbacks en simulación:
 * - GET  /api/clubs/{clubId}/economy/loans/offer
 * - POST /api/clubs/{clubId}/economy/loans (X-Request-Id)
 * - GET  /api/clubs/{clubId}/economy/loans?page={page}&size={size}
 * - GET  /api/clubs/{clubId}/economy/loans/{loanId}
 * - POST /api/clubs/{clubId}/economy/loans/{loanId}/repay (X-Request-Id)
 * - GET  /api/clubs/{clubId}/economy/loans/debt
 * - GET  /api/clubs/{clubId}/economy/summary
 */
import { apiSimulator } from './api-simulator.service';
import {
  MatchdayLoanOffer,
  ActiveMatchdayLoan,
  MatchdayLoanItem,
  AggregatedDebtResponse,
  EconomicSummaryResponse,
} from '../models/types';

export const loansApi = {
  /**
   * GET /api/clubs/{clubId}/economy/loans/offer
   * Consulta la oferta personalizada de crédito por jornada para el club
   */
  async getOffer(clubId: string): Promise<MatchdayLoanOffer> {
    const raw = await apiSimulator.callWithMockFallback<any>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/economy/loans/offer`,
      service: 'stadium-economy',
      mockName: 'loans-offer',
    });

    return normalizeLoanOffer(raw);
  },

  /**
   * POST /api/clubs/{clubId}/economy/loans
   * Contrata un préstamo por jornada.
   * Requiere cabecera idempotente X-Request-Id (UUID).
   */
  async contractLoan(
    clubId: string,
    principal: number,
    requestId: string
  ): Promise<ActiveMatchdayLoan> {
    const raw = await apiSimulator.callWithMockFallback<any>({
      method: 'POST',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/economy/loans`,
      headers: {
        'X-Request-Id': requestId,
      },
      body: {
        principal,
      },
      service: 'stadium-economy',
      mockName: 'loans-offer',
      transformMock: (mock) => {
        const rate = mock.ratePerMatchday ?? 0.01;
        const currentMd = mock.calendar?.currentMatchday ?? 10;
        const maturity = Number(mock.maturityMatchday);
        const termMatchdays = Number(mock.maximumTermMatchdays);
        return {
          id: `loan-${Date.now()}`,
          principal,
          outstandingPrincipal: principal,
          ratePerMatchday: rate,
          firstMatchday: currentMd + 1,
          maturityMatchday: maturity,
          termMatchdays,
          nextInterest: Math.round(principal * rate * 100) / 100,
          status: 'ACTIVE',
          createdAt: new Date().toISOString(),
          movements: [
            {
              id: `mov-${Date.now()}`,
              type: 'DISBURSEMENT',
              amount: principal,
              principalAmount: principal,
              matchday: currentMd,
              matchdayNumber: currentMd,
              occurredAt: new Date().toISOString(),
              description: `Concesión de crédito: ${principal.toLocaleString('es-ES')} €`,
            },
          ],
        };
      },
    });

    return normalizeActiveLoan(raw?.loan || raw);
  },

  /**
   * GET /api/clubs/{clubId}/economy/loans?page={page}&size={size}
   * Devuelve el historial de préstamos en orden descendente de concesión.
   * Nota contractual: Devuelve un Array directo sin total ni envoltorio de paginación.
   */
  async getHistory(
    clubId: string,
    page: number = 0,
    size: number = 20
  ): Promise<MatchdayLoanItem[]> {
    const raw = await apiSimulator.callWithMockFallback<any>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/economy/loans?page=${page}&size=${size}`,
      service: 'stadium-economy',
      mockName: 'loans-history',
    });

    const list: any[] = Array.isArray(raw)
      ? raw
      : raw?.content || raw?.items || raw?.loans || [];

    return list.map(normalizeLoanItem);
  },

  /**
   * GET /api/clubs/{clubId}/economy/loans/{loanId}
   * Consulta el detalle de un préstamo y todos sus movimientos
   */
  async getDetail(clubId: string, loanId: string): Promise<ActiveMatchdayLoan> {
    const raw = await apiSimulator.callWithMockFallback<any>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/economy/loans/${encodeURIComponent(loanId)}`,
      service: 'stadium-economy',
      mockName: 'loans-offer',
    });

    return normalizeActiveLoan(raw?.loan || raw);
  },

  /**
   * POST /api/clubs/{clubId}/economy/loans/{loanId}/repay
   * Amortiza capital (parcial o total) sin comisión.
   * Requiere cabecera idempotente X-Request-Id (UUID).
   */
  async repayLoan(
    clubId: string,
    loanId: string,
    amount: number,
    requestId: string
  ): Promise<ActiveMatchdayLoan> {
    const raw = await apiSimulator.callWithMockFallback<any>({
      method: 'POST',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/economy/loans/${encodeURIComponent(loanId)}/repay`,
      headers: {
        'X-Request-Id': requestId,
      },
      body: {
        amount,
      },
      service: 'stadium-economy',
      mockName: 'loans-offer',
    });

    return normalizeActiveLoan(raw?.loan || raw);
  },

  /**
   * GET /api/clubs/{clubId}/economy/loans/debt
   * Consulta la deuda agregada (créditos por jornada + deuda histórica de préstamos antiguos)
   */
  async getAggregatedDebt(clubId: string): Promise<AggregatedDebtResponse> {
    const raw = await apiSimulator.callWithMockFallback<any>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/economy/loans/debt`,
      service: 'stadium-economy',
      mockName: 'loans-debt',
    });

    return {
      totalDebt: Number(raw?.outstandingPrincipal ?? raw?.totalDebt ?? raw?.debt ?? 0),
      matchdayLoansDebt: Number(raw?.matchdayLoansDebt ?? 0),
      legacyLoansDebt: Number(raw?.legacyLoansDebt ?? 0),
      nextInstallments: Array.isArray(raw?.nextInstallments) ? raw.nextInstallments : [],
    };
  },

  /**
   * GET /api/clubs/{clubId}/economy/summary
   * Consulta el resumen económico del club con desglose de saldos (total, reservado, disponible)
   */
  async getEconomicSummary(clubId: string): Promise<EconomicSummaryResponse> {
    const raw = await apiSimulator.callWithMockFallback<any>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/economy/summary`,
      service: 'stadium-economy',
      mockName: 'economic-summary',
    });

    const cash = raw?.cash || {};
    return {
      cash: {
        totalBalance: Number(cash.totalBalance ?? raw?.totalBalance ?? 0),
        reservedBalance: Number(cash.reservedBalance ?? raw?.reservedBalance ?? 0),
        availableBalance: Number(cash.availableBalance ?? raw?.availableBalance ?? 0),
        currency: cash.currency || raw?.currency || 'EUR',
      },
      totalBalance: Number(raw?.totalBalance ?? cash.totalBalance ?? 0),
      reservedBalance: Number(raw?.reservedBalance ?? cash.reservedBalance ?? 0),
      availableBalance: Number(raw?.availableBalance ?? cash.availableBalance ?? 0),
      currency: raw?.currency || cash.currency || 'EUR',
    };
  },
};

/**
 * Normaliza la oferta de préstamo recibida del backend
 */
function normalizeLoanOffer(raw: any): MatchdayLoanOffer {
  if (!raw) {
    return {
      maximumPrincipal: 0,
      remainingMatchdays: 0,
      maximumTermMatchdays: 0,
      maturityMatchday: null,
      ratePerMatchday: 0.01,
      ineligibilityReason: null,
      activeLoan: null,
      calendar: null,
    };
  }

  const cal = raw.calendar || {};
  const remaining = Number(
    raw.remainingMatchdays ??
    (Array.isArray(cal.remaining) ? cal.remaining.length : cal.remaining) ??
    cal.remainingMatchdays ??
    0
  );

  return {
    maximumPrincipal: Number(raw.maximumPrincipal ?? 0),
    remainingMatchdays: remaining,
    maximumTermMatchdays: Number(raw.maximumTermMatchdays ?? 0),
    maturityMatchday: raw.maturityMatchday == null ? null : Number(raw.maturityMatchday),
    tier: raw.tier || 'MIDDLE',
    ratePerMatchday: Number(raw.ratePerMatchday ?? 0.01),
    ineligibilityReason: raw.ineligibilityReason || null,
    activeLoan: raw.activeLoan ? normalizeActiveLoan(raw.activeLoan) : null,
    calendar: {
      currentMatchday: cal.currentMatchday,
      remainingMatchdays: remaining,
      remaining,
      totalMatchdays: cal.totalMatchdays,
      seasonId: cal.seasonId,
      cycle: cal.cycle,
      round: cal.round,
    },
  };
}

/**
 * Normaliza un préstamo activo o su detalle
 */
function normalizeActiveLoan(raw: any): ActiveMatchdayLoan {
  if (!raw) return {} as ActiveMatchdayLoan;

  const principal = Number(raw.principal ?? raw.amount ?? 0);
  const outstanding = Number(raw.outstandingPrincipal ?? raw.remaining ?? principal);
  const rate = Number(raw.ratePerMatchday ?? raw.rate ?? 0.01);
  const movements: any[] = Array.isArray(raw.movements) ? raw.movements : [];

  return {
    id: String(raw.id || raw.loanId || `loan-${Date.now()}`),
    loanId: raw.loanId || raw.id,
    clubId: raw.clubId,
    principal,
    outstandingPrincipal: outstanding,
    ratePerMatchday: rate,
    firstMatchday: Number(raw.firstMatchday ?? 1),
    maturityMatchday: Number(raw.maturityMatchday ?? 1),
    termMatchdays: raw.termMatchdays ? Number(raw.termMatchdays) : undefined,
    nextInterest: Number(raw.nextInterest ?? Math.round(outstanding * rate * 100) / 100),
    status: String(raw.status || 'ACTIVE').toUpperCase(),
    createdAt: raw.createdAt,
    settledAt: raw.settledAt,
    movements: movements.map((m: any) => ({
      id: String(m.id || m.movementId || `mov-${Date.now()}`),
      movementId: m.movementId || m.id,
      type: String(m.type || 'INTEREST').toUpperCase(),
      amount: Number(m.amount ?? 0),
      interestAmount: m.interestAmount !== undefined ? Number(m.interestAmount) : undefined,
      principalAmount: m.principalAmount !== undefined ? Number(m.principalAmount) : undefined,
      createdAt: m.createdAt,
      date: m.createdAt || m.date || m.occurredAt,
      occurredAt: m.occurredAt || m.createdAt || m.date,
      matchday: m.matchday !== undefined ? Number(m.matchday) : (m.matchdayNumber !== undefined ? Number(m.matchdayNumber) : undefined),
      matchdayNumber: m.matchdayNumber !== undefined ? Number(m.matchdayNumber) : (m.matchday !== undefined ? Number(m.matchday) : undefined),
      description: m.description,
      balanceAfter: m.balanceAfter !== undefined ? Number(m.balanceAfter) : undefined,
    })),
  };
}

/**
 * Normaliza un elemento del historial de préstamos
 */
function normalizeLoanItem(raw: any): MatchdayLoanItem {
  const norm = normalizeActiveLoan(raw);
  return {
    id: norm.id,
    loanId: norm.loanId,
    clubId: norm.clubId,
    principal: norm.principal,
    outstandingPrincipal: norm.outstandingPrincipal,
    ratePerMatchday: norm.ratePerMatchday,
    termMatchdays: norm.termMatchdays,
    firstMatchday: norm.firstMatchday,
    maturityMatchday: norm.maturityMatchday,
    nextInterest: norm.nextInterest,
    status: norm.status,
    createdAt: norm.createdAt,
    settledAt: norm.settledAt,
    movements: norm.movements,
  };
}
